import { Component, h, State, Element, Host, Watch, Prop } from "@stencil/core"
import {
  menuFlyoutReducer,
  getInitialState,
  MenuFlyoutState,
  MenuFlyoutActions,
} from "../menu-flyout-store"
import {
  StoreConnection,
  StateHandle,
  computedProperty,
  getAppearance,
} from "../../../core/helpers/webcomponent-helpers"

@Component({
  tag: "sdx-menu-flyout-content",
  styleUrl: "menu-flyout-content.scss",
  shadow: true,
})
export class MenuFlyoutContent {
  private store: StoreConnection<this, MenuFlyoutState, MenuFlyoutActions>
  private arrowEl?: HTMLElement
  private trapFocusEl?: HTMLSdxTrapFocusElement

  @Element() public el!: HTMLSdxMenuFlyoutListElement

  @State() public state!: StateHandle<MenuFlyoutState>

  /**
   * Needed in input.tsx
   * @private
   */
  @Prop() public autofocus: boolean = true

  /**
   * @private
   */
  @Prop() public arrowStyles?: Record<
    "positionBottomLightTheme" | "positionTopDarkTheme",
    Record<string, string>
  >

  /**
   * @private
   */
  @Prop() public bodyStyle: Record<string, string | undefined> = {}

  /**
   * Needed in tour.tsx and input.tsx
   * @private
   */
  @Prop() public trapFocusFirstFocusableElement?:
    | HTMLSdxButtonElement
    | HTMLDivElement

  @Watch("state")
  public stateChanged({}, prev: StateHandle<MenuFlyoutState>) {
    const state = this.state.get()
    const prevState = prev.get()

    if (state.display !== prevState.display) {
      this.displayChanged(state)
    }
  }

  constructor() {
    this.store = new StoreConnection(
      this,
      menuFlyoutReducer,
      getInitialState(),
      ["direction", "display"],
    )
  }

  public connectedCallback() {
    // Register self
    this.store.set("contentEl", this.el)

    // Re-register arrow el (but only if already rendered)
    if (this.arrowEl) {
      this.store.dispatch({
        type: "ADD_ARROW_EL",
        arrowEl: this.arrowEl,
      })
    }
  }

  public disconnectedCallback() {
    // Unregister arrow el (but only if already rendered)
    // If a component is created and appended in direct succession (like with
    // Angular's ng-content), disconnectedCallback() is called before render()
    if (this.arrowEl) {
      this.store.dispatch({
        type: "REMOVE_ARROW_EL",
        arrowEl: this.arrowEl,
      })
    }

    // Unregister self
    this.store.set("contentEl", undefined)
  }

  public componentDidLoad() {
    // Register arrow el
    this.store.dispatch({
      type: "ADD_ARROW_EL",
      arrowEl: this.arrowEl!,
    })

    this.store.subscribe()
  }

  private getArrowStyle() {
    const { direction } = this.state.get()

    const isOpenTowardsBottom =
      direction === "bottom-left" || direction === "bottom-right"
    const isLightTheme = getAppearance(this.el) === "sdx--light-theme"

    if (isOpenTowardsBottom && isLightTheme) {
      return this.arrowStyles?.positionBottomLightTheme
    }

    if (!isOpenTowardsBottom && !isLightTheme) {
      return this.arrowStyles?.positionTopDarkTheme
    }

    return
  }

  private displayChanged(state: MenuFlyoutState) {
    if (state.display === "open" && this.autofocus) {
      this.trapFocusEl?.doFocus()
    }
  }

  private getHostClassNames() {
    return {
      ...computedProperty(this.state.get().direction),
    }
  }

  private getComponentClassNames() {
    return {
      component: true,
      [getAppearance(this.el)]: true,
    }
  }

  public render() {
    const hasSdxLabel = this.el.querySelector("#sdx-dialog-content-label")

    const hasSdxDescription = this.el.querySelector(
      "#sdx-dialog-content-description",
    )

    return (
      <Host
        class={this.getHostClassNames()}
        role="dialog"
        aria-labelledby={
          /* just for backwards compatibility */
          hasSdxLabel
            ? "sdx-dialog-content-label"
            : this.el.getAttribute("aria-labelledby")
        }
        aria-describedby={
          /* just for backwards compatibility */
          hasSdxDescription
            ? "sdx-dialog-content-description"
            : this.el.getAttribute("aria-describedby")
        }
      >
        <div class={this.getComponentClassNames()}>
          <div
            class="arrow"
            ref={(el) => (this.arrowEl = el)}
            style={this.getArrowStyle()}
          />

          <div class="body" style={this.bodyStyle}>
            <sdx-trap-focus
              ref={(el) => (this.trapFocusEl = el)}
              lang={this.el.lang}
              firstFocusableElement={this.trapFocusFirstFocusableElement}
            >
              <slot />
            </sdx-trap-focus>
          </div>
        </div>
      </Host>
    )
  }
}
